/**
 * The three things that need confirming, and nothing else.
 *
 * Rename validates against the SAME rules the server enforces, so the error
 * appears as you type rather than after a round trip. Delete states exactly
 * what will happen, including how many files a folder contains, because an
 * engineering archive is not a place for surprises.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import * as Dialog from '@radix-ui/react-dialog'
import { AlertTriangle, Upload as UploadIcon, FolderUp } from 'lucide-react'
import { renameLeaf, parsePath, basename, type ProjectId, type Path } from '@lattice/core'
import { useWorkspace, filesByProject } from '@/store/workspace'
import { useUi } from '@/store/ui'
import { api } from '@/store/api'
import { Button, Spinner } from './ui/primitives'
import { formatBytes, pluralise } from '@/lib/format'

function Frame({ title, children, onClose, width = 'max-w-md' }: {
  title: string; children: React.ReactNode; onClose: () => void; width?: string
}) {
  return (
    <Dialog.Root open onOpenChange={o => !o && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/40 fade-in" />
        <Dialog.Content
          className={`pop-in fixed left-1/2 top-[22%] z-50 w-[calc(100%-2rem)] ${width} -translate-x-1/2 rounded-[var(--radius-lg)] border border-[var(--border)] bg-[var(--bg-raised)] shadow-modal`}
        >
          <Dialog.Title className="border-b border-[var(--border)] px-4 py-2.5 text-sm font-semibold">
            {title}
          </Dialog.Title>
          {children}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}

export function RenameDialog({ projectId, path, currentName, onClose }: {
  projectId: ProjectId; path: string; currentName: string; onClose: () => void
}) {
  const changes = useWorkspace(s => s.changes)
  const { open } = useUi()
  const [name, setName] = useState(currentName)
  const [busy, setBusy] = useState(false)
  const [serverError, setServerError] = useState<string>()
  const ref = useRef<HTMLInputElement>(null)

  // Select the stem, not the extension: renaming almost never means changing
  // ".SLDPRT", and preselecting it would make the common case destructive.
  useEffect(() => {
    const input = ref.current
    if (!input) return
    input.focus()
    const dot = currentName.lastIndexOf('.')
    input.setSelectionRange(0, dot > 0 ? dot : currentName.length)
  }, [currentName])

  const files = useMemo(() => filesByProject(changes).get(projectId), [changes, projectId])

  /** The same checks the server will make, run as you type. */
  const problem = useMemo((): string | undefined => {
    if (name === currentName) return undefined
    const renamed = renameLeaf(path as Path, name)
    if (!renamed.ok) return renamed.error
    const target = renamed.value.toLowerCase()
    if (target !== path.toLowerCase() && files?.has(target)) return `"${name}" already exists here`
    return undefined
  }, [name, currentName, path, files])

  const submit = useCallback(async () => {
    if (problem || name === currentName) { onClose(); return }
    setBusy(true)
    setServerError(undefined)
    try {
      const result = await api.renameFile({ projectId, path, newName: name })
      const moved = result.changes[0]?.path
      if (moved) open(moved)
      onClose()
    } catch (e) {
      setServerError((e as Error).message)
      setBusy(false)
    }
  }, [problem, name, currentName, projectId, path, onClose, open])

  return (
    <Frame title="Rename" onClose={onClose}>
      <form onSubmit={e => { e.preventDefault(); void submit() }} className="p-4">
        <label className="block text-xs text-[var(--fg-muted)]" htmlFor="rename-input">New name</label>
        <input
          id="rename-input"
          ref={ref}
          value={name}
          onChange={e => setName(e.target.value)}
          className="mt-1 w-full rounded-[var(--radius-md)] border border-[var(--border)] bg-[var(--bg-inset)] px-2 py-1.5 text-sm outline-none focus:border-[var(--border-strong)]"
        />
        <p className="mt-1.5 h-4 text-xs text-[var(--deleted)]">{problem ?? serverError ?? ''}</p>
        <p className="text-[11px] text-[var(--fg-subtle)]">
          Renaming records a change; nothing is copied and the history is kept.
        </p>
        <div className="mt-3 flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="primary" disabled={Boolean(problem) || busy}>
            {busy && <Spinner size={12} />} Rename
          </Button>
        </div>
      </form>
    </Frame>
  )
}

export function DeleteDialog({ projectId, paths, onClose }: {
  projectId: ProjectId; paths: string[]; onClose: () => void
}) {
  const changes = useWorkspace(s => s.changes)
  const { clearSelection, open, openPath } = useUi()
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string>()

  // Expand folders so the person is told the true blast radius.
  const affected = useMemo(() => {
    const files = filesByProject(changes).get(projectId)
    if (!files) return []
    return [...files.values()].filter(f =>
      paths.some(p => f.path === p || f.path.startsWith(`${p}/`)))
  }, [changes, projectId, paths])

  const bytes = affected.reduce((sum, f) => sum + f.size, 0)

  const submit = async (): Promise<void> => {
    setBusy(true)
    setError(undefined)
    try {
      await api.deleteFiles({ projectId, paths })
      clearSelection()
      if (openPath && affected.some(f => f.path === openPath)) open(undefined)
      onClose()
    } catch (e) {
      setError((e as Error).message)
      setBusy(false)
    }
  }

  return (
    <Frame title={`Delete ${pluralise(affected.length, 'file')}?`} onClose={onClose}>
      <div className="p-4">
        <div className="flex gap-2.5">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-[var(--modified)]" strokeWidth={1.75} />
          <div className="min-w-0 text-sm">
            <p>
              {affected.length === 1
                ? <>“{basename(affected[0].path)}” will be removed from this project.</>
                : <>{pluralise(affected.length, 'file')} ({formatBytes(bytes)}) will be removed from this project.</>}
            </p>
            <p className="mt-1 text-[var(--fg-muted)]">
              The history is kept — the deletion is recorded with your name, and the content
              stays in storage — but the files will no longer appear in the tree.
            </p>
          </div>
        </div>

        {affected.length > 1 && (
          <ul className="mt-3 max-h-40 overflow-y-auto rounded-[var(--radius-md)] border border-[var(--border)] bg-[var(--bg-inset)] p-1.5 text-xs">
            {affected.slice(0, 200).map(f => (
              <li key={f.path} className="truncate py-0.5 text-[var(--fg-muted)]">{f.path}</li>
            ))}
            {affected.length > 200 && <li className="py-0.5 text-[var(--fg-subtle)]">…and {affected.length - 200} more</li>}
          </ul>
        )}

        {error && <p className="mt-2 text-xs text-[var(--deleted)]">{error}</p>}

        <div className="mt-3 flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button variant="danger" onClick={() => void submit()} disabled={busy || affected.length === 0}>
            {busy && <Spinner size={12} />} Delete
          </Button>
        </div>
      </div>
    </Frame>
  )
}

export function UploadDialog({ projectId, onClose }: { projectId: ProjectId; onClose: () => void }) {
  const fileRef = useRef<HTMLInputElement>(null)
  const folderRef = useRef<HTMLInputElement>(null)
  const [prefix, setPrefix] = useState('')

  const send = useCallback((list: FileList | null) => {
    if (!list || list.length === 0) return
    const base = prefix.trim().replace(/^\/+|\/+$/g, '')
    const items = [...list].map(file => {
      const relative = (file as File & { webkitRelativePath?: string }).webkitRelativePath || file.name
      const full = base ? `${base}/${relative}` : relative
      return { file, path: parsePath(full).ok ? full : file.name }
    })
    void import('@/lib/transfer').then(m => m.uploadFiles(projectId, items))
    onClose()
  }, [projectId, prefix, onClose])

  return (
    <Frame title="Upload to this project" onClose={onClose}>
      <div className="p-4">
        <label className="block text-xs text-[var(--fg-muted)]" htmlFor="upload-prefix">
          Folder (optional)
        </label>
        <input
          id="upload-prefix"
          value={prefix}
          onChange={e => setPrefix(e.target.value)}
          placeholder="e.g. Gun_Housing_Assembly/Drawing"
          className="mt-1 w-full rounded-[var(--radius-md)] border border-[var(--border)] bg-[var(--bg-inset)] px-2 py-1.5 text-sm outline-none focus:border-[var(--border-strong)]"
        />

        <div className="mt-3 grid grid-cols-2 gap-2">
          <Button variant="secondary" size="lg" onClick={() => fileRef.current?.click()}>
            <UploadIcon className="h-4 w-4" strokeWidth={1.75} /> Choose files
          </Button>
          <Button variant="secondary" size="lg" onClick={() => folderRef.current?.click()}>
            <FolderUp className="h-4 w-4" strokeWidth={1.75} /> Choose folder
          </Button>
        </div>

        <p className="mt-3 text-xs text-[var(--fg-subtle)]">
          Files are matched by content, so anything already in the workspace is recorded
          instantly without uploading a single byte. You can also drag files onto the tree.
        </p>

        <input ref={fileRef} type="file" multiple hidden onChange={e => send(e.target.files)} />
        <input
          ref={folderRef} type="file" hidden onChange={e => send(e.target.files)}
          {...({ webkitdirectory: '', directory: '' } as Record<string, string>)}
        />
      </div>
    </Frame>
  )
}
